import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useLocalNotificationBanner } from "Shared/util/custom-hooks";
import {
    PayoutInformation,
    PayoutInformationAccount,
} from "Shared/util/types/api";
import {
    getPayoutInformation,
    listPayoutInformationCurrencies,
} from "Shared/util/web-api";
import { PayoutInformationContext } from "./PayoutInformationContext";
import { PayoutInformationContextValues } from "./types";

export const PayoutInformationContextProvider: FC<PropsWithChildren<{}>> = ({
    children,
}) => {
    const { NotificationBannerType, addLocalNotificationBanner } =
        useLocalNotificationBanner();

    const [isLoading, setIsLoading] = useState(true);
    const [loadingFailed, setLoadingFailed] = useState(false);
    const [values, setValues] = useState<PayoutInformationContextValues>({
        accounts: [],
        bankConsents: [],
        currencies: [],
    });

    useEffect(() => {
        Promise.all([getPayoutInformation(), listPayoutInformationCurrencies()])
            .then(([payoutInformationResult, currenciesResult]) =>
                setValues({
                    accounts: payoutInformationResult.accounts ?? [],
                    bankConsents: payoutInformationResult.bankConsents ?? [],
                    currencies: currenciesResult ?? [],
                }),
            )
            .catch(() => {
                addLocalNotificationBanner(
                    (tnb) => tnb.a_technical_error_occurred,
                    NotificationBannerType.Error,
                );
                setLoadingFailed(true);
            })
            .finally(() => setIsLoading(false));
    }, [addLocalNotificationBanner]);

    const setPayoutInformationState = useCallback(
        (newPayoutInformation: PayoutInformation) =>
            setValues(
                (prev): PayoutInformationContextValues => ({
                    ...prev,
                    accounts: newPayoutInformation?.accounts ?? [],
                    bankConsents: newPayoutInformation?.bankConsents ?? [],
                }),
            ),
        [],
    );

    const reloadPayoutInformation = useCallback(
        () =>
            getPayoutInformation().then((result) =>
                setPayoutInformationState(result),
            ),
        [setPayoutInformationState],
    );

    const setPayoutInformationAccountsState = useCallback(
        (
            newAccounts:
                | PayoutInformationAccount[]
                | ((
                      prevState: PayoutInformationAccount[],
                  ) => PayoutInformationAccount[]),
        ) =>
            setValues(
                (prev): PayoutInformationContextValues => ({
                    ...prev,
                    accounts:
                        typeof newAccounts === "function"
                            ? newAccounts(prev.accounts)
                            : newAccounts,
                }),
            ),
        [],
    );

    return (
        <PayoutInformationContext.Provider
            value={{
                isLoading,
                loadingFailed,
                reloadPayoutInformation: reloadPayoutInformation,
                setPayoutAccountsState: setPayoutInformationAccountsState,
                setPayoutInformationState: setPayoutInformationState,
                values,
            }}
        >
            {children}
        </PayoutInformationContext.Provider>
    );
};
